import { isEmpty, lowerCase } from "lodash-es";
import { HealthHazardIdentifier, PhysicalHazardIdentifier } from "../types";
import {
  PressureDescriptions,
  StorageTypeDescriptions,
  TemperatureDescriptions,
} from "../constants/tierii";
import { CredentialKind } from "encamp-prisma/generated/client";

export const ASMARK_STATE_SPECIFIC_PRODUCT_MAPPERS: StateProductMap = {
  AZ: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  DE: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  KS: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  KY: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  MO: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  NJ: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  SD: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  TX: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
  WA: {
    "bin, bay or pile": "Other",
    minibulk: "Other",
  },
};

export type StateProductMap = Partial<
  Record<
    string,
    {
      [key: string]:
        | StorageTypeDescriptions
        | PressureDescriptions
        | TemperatureDescriptions;
    }
  >
>;

export const STATE_SPECIFIC_PRODUCT_MAPPERS: StateProductMap[] = [
  ASMARK_STATE_SPECIFIC_PRODUCT_MAPPERS,
];

const ASMARK_STORAGE_MAPPERS: {
  [key: string]: StorageTypeDescriptions;
} = {
  "sulfuric acid": "Other", // questionable?
  "tank inside a building": "Tank inside building",
  "plastic drum": "Plastic or non-metallic drum",
  "plastic jugs": "Plastic bottles or jugs",
};

const LEGACY_PRESSURE_MAPPERS: {
  [key: string]: PressureDescriptions;
} = {
  "1": "Ambient pressure",
  "2": "Greater than ambient pressure",
  "3": "Less than ambient pressure",
};

const LEGACY_TEMPERATURE_MAPPERS: {
  [key: string]: TemperatureDescriptions;
} = {
  "4": "Ambient temperature",
  "5": "Cryogenic conditions",
  "6": "Greater than ambient temperature",
  "7": "Less than ambient temperature but not cryogenic",
};

const STEERS_TEMP_MAPPERS: {
  [key: string]: TemperatureDescriptions;
} = {
  "less than ambient temp. / not cryog":
    "Less than ambient temperature but not cryogenic",
};

// These are non-standard fun times!
const ADDITIONAL_PRODUCT_MAPPERS: {
  [key: string]:
    | StorageTypeDescriptions
    | PressureDescriptions
    | TemperatureDescriptions;
} = {
  ...ASMARK_STORAGE_MAPPERS,
  ...STEERS_TEMP_MAPPERS,

  ...LEGACY_PRESSURE_MAPPERS,
  ...LEGACY_TEMPERATURE_MAPPERS,
};

export function parseXmlBoolean(
  value: "true" | "false" | "Yes" | "1" | "0" | "No" | undefined
): boolean {
  return value === "true" || value === "1" || value === "Yes";
}

export const safeParseFloat = (
  value: string | null | undefined
): number | null => {
  if (!value) {
    return null;
  }
  const cleanedValue = value.replace(/,/g, "").trim();
  // ast-grep-ignore: no-unsafe-number-parsing
  const parsed = parseFloat(cleanedValue);
  if (isNaN(parsed)) {
    return null;
  }
  return parsed;
};

export const safeParseInt = (
  value: string | null | undefined
): number | null => {
  if (!value) {
    return null;
  }
  const cleanedValue = value.replace(/,/g, "").trim();
  // ast-grep-ignore: no-unsafe-number-parsing
  const parsed = parseInt(cleanedValue);
  if (isNaN(parsed)) {
    return null;
  }
  return parsed;
};

export function getHazards(
  hazardMap: {
    [key: string]: PhysicalHazardIdentifier | HealthHazardIdentifier;
  },
  chemical: any
): PhysicalHazardIdentifier[] | HealthHazardIdentifier[] {
  /* it is possible for a <chemical>...</chemical> to have no
   * <hazards>...</hazards> subelement, which causes
   * chemical.hazards to be undefined when we parse the
   * raw xml string with xml2js's Parser#parseStringPromise()
   */
  if (isEmpty(chemical.hazards)) return [];

  return Object.entries(hazardMap).reduce((memo, [t2sKey, encampKey]) => {
    const hazard = chemical.hazards[0].hazard.find(
      (haz: any) => haz.category[0] === t2sKey
    );
    return hazard && parseXmlBoolean(hazard.value[0])
      ? [...memo, encampKey]
      : memo;
  }, [] as any);
}

export function standardizeTemperature(value: string) {
  // Important!!! lodash-es lowerCase strips special characters
  const lowerCaseValue = lowerCase(value);
  if (/.*cryogenic conditions$/.test(lowerCaseValue)) {
    return "Cryogenic conditions";
  }

  if (/.*less than ambient temperature$/.test(lowerCaseValue)) {
    return "Less than ambient temperature";
  }

  if (/.*greater than ambient temperature$/.test(lowerCaseValue)) {
    return "Greater than ambient temperature";
  }

  if (
    /.*less than ambient temperature but not cryogenic$/.test(lowerCaseValue)
  ) {
    return "Less than ambient temperature but not cryogenic";
  }

  // TX T2S
  if (/.*less than ambient temp not cryog$/.test(lowerCaseValue)) {
    return "Less than ambient temperature but not cryogenic";
  }

  if (
    /.*(?!(greater|less) than )ambient( temperature)?(?! but not cryogenic).*/.test(
      lowerCaseValue
    )
  ) {
    return "Ambient temperature";
  }

  throw new Error(`unidentifiable temperature: ${value}`);
}

export function standardizePressure(value: string) {
  const lowerCaseValue = lowerCase(value);
  if (/.*less than ambient pressure$/.test(lowerCaseValue))
    return "Less than ambient pressure";

  if (/.*greater than ambient pressure$/.test(lowerCaseValue))
    return "Greater than ambient pressure";

  if (/.*(?!(greater|less) than )ambient( pressure)?.*/.test(lowerCaseValue))
    return "Ambient pressure";

  throw new Error(`unidentifiable pressure: ${value}`);
}

export function constantMapper(
  constants: readonly string[],
  value: string,
  state?: string | undefined | null
): string {
  const lowerCaseValue = value?.toLowerCase() ?? "";

  // Sometimes portals include additional strings, aka "B - Below Ground Tank"
  // where Encamp would like to represent this as "Below Ground Tank"
  for (const c of constants) {
    if (new RegExp(c.toLowerCase()).test(lowerCaseValue)) {
      return c;
    }
  }

  for (const [key, value] of Object.entries(ADDITIONAL_PRODUCT_MAPPERS)) {
    if (new RegExp(key.toLowerCase()).test(lowerCaseValue)) {
      return value;
    }
  }

  // if a state is provided, lets check whether there is a state specific entry for it
  const val = getStateSpecificProductMappingIfExists(value, state);
  if (val) {
    return val;
  }

  return lowerCaseValue;
}

export function getStateSpecificProductMappingIfExists(
  value: string,
  state?: string | undefined | null
) {
  if (!state) {
    return null;
  }

  const lowerCaseValue = value.toLowerCase() ?? "";
  for (const mapper of STATE_SPECIFIC_PRODUCT_MAPPERS) {
    const stateMapper = mapper[state];
    if (stateMapper) {
      for (const key in stateMapper) {
        if (new RegExp(key.toLowerCase()).test(lowerCaseValue)) {
          return stateMapper[key];
        }
      }
    }
  }

  return null;
}

export function transformStringToNumberOrNull(
  value: string | null | undefined
): number | null {
  // ast-grep-ignore: no-unsafe-number-parsing
  return value ? Number(value.replace(/,/g, "")) : null;
}

export function transformStringToNumber(
  value: string | null | undefined
): number {
  if (!value) {
    return 0;
  }
  // ast-grep-ignore: no-unsafe-number-parsing
  return Number(value.replace(/,/g, ""));
}

export function getCredentialResourceId<
  T extends {
    username: string;
    kind: CredentialKind;
    facility: { id: string; state: string; county: string | null };
  }
>(credential: T) {
  const jurisdiction = getJurisdiction(credential.kind, credential.facility);
  return `${jurisdiction}-${credential.username}`;
}

function getJurisdiction<
  T extends { id: string; state: string; county: string | null }
>(kind: CredentialKind, { id, state, county }: T) {
  // Virginia has T2M instances in Henrico and Fairfax county
  // We don't want to lock on a resource like VA_EncampUser
  // because we can have two reports running simultaneously
  // one in Henrico County's instance, and one in Fairfax's
  if (state === "VA") {
    if (county) return `${state}_${county}`;
    console.warn("Facility is in VA but does not have a county", {
      facilityId: id,
    });
  }

  // if this is an LEPC credential, we want to create a lock
  // based on the county the facility is in, not just the state
  if (kind === CredentialKind.LEPC) return `${state}_${county}`;

  return state;
}
